/**
 * Enhanced Enquiry Data Model
 * Full CRM pipeline management for lead-to-resident journey
 */

export const createEnquiry = (data = {}) => {
  return {
    id: data.id || `enq-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    
    // Basic Information
    name: data.name || '',
    age: data.age || null,
    phone: data.phone || '',
    email: data.email || '',
    address: data.address || null,
    
    // Pipeline Management
    status: data.status || 'new', // new, contacted, assessment, waitlisted, admitted, declined
    stage: data.stage || 'enquiry', // enquiry, tour, application, assessment, agreement, admission
    priority: data.priority || 'medium', // low, medium, high, critical
    leadScore: data.leadScore || 0, // 0-100
    
    // Source & Channel
    source: data.source || 'unknown', // website, phone, referral, walk_in, my_aged_care
    channel: data.channel || 'phone', // website, phone, email, walk_in
    referralPartner: data.referralPartner || null,
    myAgedCareReferralId: data.myAgedCareReferralId || null,
    
    // Care Needs
    careLevel: data.careLevel || null, // low, medium, high, dementia, palliative
    primaryConditions: data.primaryConditions || [],
    urgency: data.urgency || null,
    immediatePlacement: data.immediatePlacement ?? false,
    
    // Family/Representative
    familyContacts: data.familyContacts || [],
    primaryContact: data.primaryContact || null,
    decisionMaker: data.decisionMaker || null,
    
    // Financial
    financialEligibility: data.financialEligibility || null, // assessed, eligible, ineligible, pending
    radCapacity: data.radCapacity || null, // Refundable Accommodation Deposit capacity
    dapCapacity: data.dapCapacity || null, // Daily Accommodation Payment capacity
    meansTested: data.meansTested ?? false,
    
    // Facility Preferences
    preferredFacility: data.preferredFacility || null,
    preferredFacilities: data.preferredFacilities || [],
    preferredWard: data.preferredWard || null,
    
    // Tour & Assessment
    tourScheduled: data.tourScheduled || null,
    tourCompleted: data.tourCompleted || null,
    tourType: data.tourType || null, // general, specialized, virtual
    assessmentScheduled: data.assessmentScheduled || null,
    assessmentCompleted: data.assessmentCompleted || null,
    
    // Documents & Checklist
    documents: data.documents || [],
    checklist: data.checklist || {
      medicalAssessment: { required: true, received: false },
      acatAssessment: { required: true, received: false },
      financialAssessment: { required: true, received: false },
      familyStatement: { required: false, received: false },
      powerOfAttorney: { required: false, received: false }
    },
    
    // Waitlist
    waitlisted: data.waitlisted ?? false,
    waitlistDate: data.waitlistDate || null,
    waitlistPosition: data.waitlistPosition || null,
    waitlistPriority: data.waitlistPriority || null,
    
    // Admission
    admissionDate: data.admissionDate || null,
    agreementGenerated: data.agreementGenerated ?? false,
    agreementSigned: data.agreementSigned ?? false,
    
    // Assignment & Follow-up
    assignedTo: data.assignedTo || null,
    lastContact: data.lastContact || null,
    nextFollowUp: data.nextFollowUp || null,
    followUpNotes: data.followUpNotes || [],
    
    // Conversion Tracking
    enquiryDate: data.enquiryDate || new Date().toISOString().split('T')[0],
    firstContactDate: data.firstContactDate || null,
    tourDate: data.tourDate || null,
    applicationDate: data.applicationDate || null,
    admissionDate: data.admissionDate || null,
    conversionTime: data.conversionTime || null, // Days from enquiry to admission
    
    // Notes & History
    notes: data.notes || '',
    history: data.history || [], // Timeline of status changes
    
    // Metadata
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString(),
    createdBy: data.createdBy || null
  };
};

/**
 * Calculate lead score
 */
export const calculateLeadScore = (enquiry) => {
  let score = 0;
  
  // Care needs match (0-30 points)
  if (enquiry.careLevel === 'high' || enquiry.careLevel === 'palliative') {
    score += 30;
  } else if (enquiry.careLevel === 'medium') {
    score += 20;
  } else if (enquiry.careLevel === 'low') {
    score += 10;
  }
  
  // Financial eligibility (0-25 points)
  if (enquiry.financialEligibility === 'eligible') {
    score += 25;
  } else if (enquiry.financialEligibility === 'assessed') {
    score += 15;
  } else if (enquiry.radCapacity && enquiry.radCapacity > 0) {
    score += 10;
  }
  
  // Urgency (0-20 points)
  if (enquiry.immediatePlacement) {
    score += 20;
  } else if (enquiry.urgency === 'immediate') {
    score += 15;
  } else if (enquiry.urgency === 'urgent') {
    score += 10;
  }
  
  // Family engagement (0-15 points)
  if (enquiry.familyContacts && enquiry.familyContacts.length > 0) {
    score += 10;
    if (enquiry.primaryContact) {
      score += 5;
    }
  }
  
  // Source quality (0-10 points)
  if (enquiry.source === 'my_aged_care' || enquiry.source === 'referral') {
    score += 10;
  } else if (enquiry.source === 'website') {
    score += 5;
  }
  
  return Math.min(score, 100); // Cap at 100
};

/**
 * Get pipeline stage from status
 */
export const getPipelineStage = (status) => {
  const stageMap = {
    'new': 'enquiry',
    'contacted': 'enquiry',
    'tour_scheduled': 'tour',
    'tour_completed': 'tour',
    'application': 'application',
    'assessment': 'assessment',
    'waitlisted': 'waitlist',
    'agreement': 'agreement',
    'admitted': 'admission',
    'declined': 'closed'
  };
  
  return stageMap[status] || 'enquiry';
};

/**
 * Calculate conversion metrics
 */
export const calculateConversionMetrics = (enquiry) => {
  const metrics = {
    enquiryToContact: null,
    contactToTour: null,
    tourToApplication: null,
    applicationToAdmission: null,
    enquiryToAdmission: null
  };
  
  if (enquiry.enquiryDate && enquiry.firstContactDate) {
    const days = Math.ceil(
      (new Date(enquiry.firstContactDate) - new Date(enquiry.enquiryDate)) / (1000 * 60 * 60 * 24)
    );
    metrics.enquiryToContact = days;
  }
  
  if (enquiry.firstContactDate && enquiry.tourDate) {
    const days = Math.ceil(
      (new Date(enquiry.tourDate) - new Date(enquiry.firstContactDate)) / (1000 * 60 * 60 * 24)
    );
    metrics.contactToTour = days;
  }
  
  if (enquiry.tourDate && enquiry.applicationDate) {
    const days = Math.ceil(
      (new Date(enquiry.applicationDate) - new Date(enquiry.tourDate)) / (1000 * 60 * 60 * 24)
    );
    metrics.tourToApplication = days;
  }
  
  if (enquiry.applicationDate && enquiry.admissionDate) {
    const days = Math.ceil(
      (new Date(enquiry.admissionDate) - new Date(enquiry.applicationDate)) / (1000 * 60 * 60 * 24)
    );
    metrics.applicationToAdmission = days;
  }
  
  if (enquiry.enquiryDate && enquiry.admissionDate) {
    const days = Math.ceil(
      (new Date(enquiry.admissionDate) - new Date(enquiry.enquiryDate)) / (1000 * 60 * 60 * 24)
    );
    metrics.enquiryToAdmission = days;
    enquiry.conversionTime = days;
  }
  
  return metrics;
};

export default {
  createEnquiry,
  calculateLeadScore,
  getPipelineStage,
  calculateConversionMetrics
};
